import * as Sentry from '@sentry/react'
import { domains } from '~/constants/domains'
import { getBuildEnvironment } from '~/utils/common'

import { isBotUserAgent } from '@mangovoice/core'

/**
 * Sentry tracing sample rates (observability RFC, PE-28 Phase 2).
 *
 * The span budget is small and shared org-wide, so sampling is opt-in by
 * design: only the ops listed below get a non-zero production rate, and
 * anything new stays at 0 until a rate is deliberately added.
 *
 * Volume anchor (July 2026, PostHog volumes ×20 for the 5% capture rollout):
 * ~13M pageload+navigation transactions/mo across web + desktop at full
 * traffic, plus ~670k targeted flow executions/mo, so the rates below admit
 * roughly 165k transactions/mo. Watch Stats → Spans in Sentry after ramping;
 * per-DSN rate limits in Sentry project settings are the hard backstop.
 */

/** Sentry span status codes; @sentry/react does not re-export them from core. */
const SPAN_STATUS_OK = 1
const SPAN_STATUS_ERROR = 2

/** Shared op for every `TracedFlow` root span; the sampler keys on it. */
const FLOW_OP = 'mango.flow'

/**
 * Root-span names for the traced flows (PE-40).
 *
 * Scope note: mango-api is not instrumented and will not be (PE-49/PE-50 are
 * canceled), so the four HTTP flows below buy the browser-side waterfall only
 * — the `http.client` child spans the SDK adds, showing where a slow request
 * went (DNS, TLS, time to first byte) up to the API boundary. Correlation past
 * that boundary is the shared session id from PE-41, not a trace. Call setup
 * is unaffected: it runs over the SIP WebSocket and never had a backend leg.
 */
export const TracedFlow = {
  Login: 'auth.login',
  TokenRefresh: 'auth.token_refresh',
  MessageSend: 'msg.send',
  ClickToDial: 'c2c.originate',
  WebphoneCallSetup: 'webphone.call_setup',
} as const

export type TracedFlowName = (typeof TracedFlow)[keyof typeof TracedFlow]

const PROD_OP_SAMPLE_RATES: Record<string, number> = {
  pageload: 0.01,
  // Sampled lower than pageload: navigations dominate volume because desktop
  // users keep the app open (and clicking around) all day.
  navigation: 0.005,
  // Targeted flows are the value tier, so they carry the highest rate: ~670k
  // executions/mo at 100%, which 0.1 keeps well inside the budget.
  [FLOW_OP]: 0.1,
}

export const tracesSampler: NonNullable<
  Sentry.BrowserOptions['tracesSampler']
> = ({ attributes }) => {
  // Bots (uptime checks, crawlers) would emit pageload traces 24/7.
  if (isBotUserAgent(navigator.userAgent)) {
    return 0
  }

  switch (getBuildEnvironment()) {
    case 'staging':
      // Staging volume is tiny and it's where tracing changes get verified.
      return 1
    case 'production': {
      const op = attributes?.[Sentry.SEMANTIC_ATTRIBUTE_SENTRY_OP]
      return typeof op === 'string' ? (PROD_OP_SAMPLE_RATES[op] ?? 0) : 0
    }
    default:
      // Local dev: quota-free by default; bump temporarily when testing.
      return 0
  }
}

/**
 * Hosts that receive trace headers (`sentry-trace`, `baggage`, and the W3C
 * `traceparent`). Attachment happens on every matching request regardless of
 * the sampling decision, so this list is a CORS contract, not a sampling one:
 * a host that does not allow these header names fails the preflight, which
 * breaks real requests rather than just losing telemetry. Add a host only
 * after checking its `Access-Control-Allow-Headers`.
 *
 * The api-gateway is the only entry. It answers preflights with
 * `Access-Control-Allow-Headers: *` on both `customer-api.mangovoice.com` and
 * `gateway-staging.mangovoice.com` (verified 2026-09-09), so it needs no
 * backend change.
 *
 * Deliberately absent:
 * - mango-api, which reads no trace headers and will not be instrumented
 *   (PE-49/PE-50 canceled). Its allow list has no `traceparent`, so targeting
 *   it would fail the preflight app-wide.
 * - music-service, which no code in this repo calls and whose allow list is
 *   therefore unverified — it would be an unchecked CORS contract for no gain.
 *
 * The SIP and chat-rtm WebSockets, PostHog, Sentry ingest, and CloudWatch
 * are excluded by construction — matching is against the request URL and none
 * of them share an origin with the gateway.
 *
 * Staging exception: the staging gateway (`gateway-staging.mangovoice.com`)
 * enforces a ~1.5 KB budget on non-`Authorization` request headers over its
 * HTTP/2 edge and answers anything over it with `431 Request Header Fields Too
 * Large` (verified 2026-09-14 — production `customer-api.mangovoice.com` is
 * not affected). The ~285 B of `sentry-trace` + `baggage` + `traceparent`
 * propagation headers are enough to push hotels-service calls over that
 * budget, so no `/hotels/v1/*` request succeeds while they attach. We key off
 * the resolved gateway host rather than the build environment so this also
 * covers local dev pointed at staging (`api_env=staging`). Production keeps
 * the browser→gateway trace leg, which is where the span budget matters.
 */
const STAGING_GATEWAY_HOST = 'gateway-staging.mangovoice.com'

const isStagingGateway = (gateway: string): boolean => {
  try {
    return new URL(gateway).hostname === STAGING_GATEWAY_HOST
  } catch {
    // Not an absolute URL (shouldn't happen for domains.GATEWAY()); treat as
    // non-staging so production propagation is never silently dropped.
    return false
  }
}

export const getTracePropagationTargets = (): string[] => {
  const gateway = domains.GATEWAY()
  return isStagingGateway(gateway) ? [] : [gateway]
}

/**
 * Runs `fn` inside a root span for a traced flow.
 *
 * `startNewTrace` is what makes the flow's own sample rate apply: a child of
 * the active pageload/navigation trace would inherit that trace's decision,
 * which is negative ~99% of the time at the browsing rates above.
 *
 * No-ops gracefully — the span is non-recording when unsampled or before init.
 *
 * Known imprecision: the browser SDK has no async-local isolation, so the
 * forked scope stays current until `fn` settles and the flow span is globally
 * active for that whole window. Any request another part of the app starts
 * meanwhile is parented under it — most visibly for `auth.token_refresh`,
 * which the 401 interceptor fires while other calls are in flight. Accepted
 * rather than worked around: the SDK only emits `http.client` children while a
 * span is active, so dropping the active span to get clean parenting would
 * drop the request waterfall these flows exist for. It does mean a flow
 * transaction can carry more than its own one child span.
 *
 * PHI rule: span names come from `TracedFlow`, and attributes must stay
 * PHI-clean (never phone numbers, message bodies, emails, or tokens).
 */
export const traceFlow = <T>(
  name: TracedFlowName,
  fn: (span: Sentry.Span) => T,
  attributes?: Record<string, string | number | boolean>,
): T =>
  Sentry.startNewTrace(() =>
    Sentry.startSpan(
      { name, op: FLOW_OP, forceTransaction: true, attributes },
      fn,
    ),
  )

/**
 * Event-driven variant for a flow that spans several callbacks (SIP call
 * setup). The caller owns the span and must `end()` it on every terminal path.
 */
export const startFlowSpan = (
  name: TracedFlowName,
  attributes?: Record<string, string | number | boolean>,
): Sentry.Span =>
  Sentry.startNewTrace(() =>
    Sentry.startInactiveSpan({
      name,
      op: FLOW_OP,
      forceTransaction: true,
      attributes,
    }),
  )

/** Span status helper: apisauce reports transport success as `ok`. */
export const setSpanOutcome = (span: Sentry.Span, ok: boolean): void => {
  span.setStatus(
    ok
      ? { code: SPAN_STATUS_OK }
      : { code: SPAN_STATUS_ERROR, message: 'internal_error' },
  )
}
